/**
 * Utility functions for embed block.
 */
import parse from 'html-react-parser'

/**
 * Determines the embed type from the block content.
 *
 * @param {string} blockContent The block html.
 * @returns {string} The embed type.
 */
export function getEmbedTypeFromBlockContent(blockContent) {
    return blockContent.includes('instagram')
        ? 'instagram'
        : blockContent.includes('flickr')
        ? 'flickr'
        : 'base'
}

/**
 * Get the Flickr URL from the block content.
 *
 * @param {string} blockContent The block content of a flickr embed block.
 *
 * This function assumes this is the block_content format from WordPress:
 *
 * <figure class="wp-block-embed is-type-rich is-provider-flickr wp-block-embed-flickr">
 * 	<div class="wp-block-embed__wrapper">
 * 		{user_provided_url}
 * 	</div>
 * </figure>
 */
export function getEmdeddableFlickrURLFromBlockContent(blockContent) {
    const parsedContent = parse(blockContent)
    const parsedContentChildren = parsedContent?.props?.children

    if (!parsedContentChildren) {
        return null
    }

    let userProvidedURL = null

    /**
     * If caption exists, then the children is an array
     * and we want the first child.
     */
    if (Array.isArray(parsedContentChildren)) {
        userProvidedURL = parsedContentChildren[0].props?.children
    } else {
        userProvidedURL = parsedContentChildren.props?.children
    }

    return convertFlickrUrlToEmbeddableURL(userProvidedURL)
}

/**
 * Converts a Flickr URL from WordPress to an iFrame embeddable URL.
 *
 * @param {string} url The Flickr URL to convert.j
 * @returns {string|null} The converted URL, or null if the URL is not an album URL.
 */
function convertFlickrUrlToEmbeddableURL(url) {
    if (typeof url !== 'string') {
        return null
    }

    return convertFlickrAlbumUrl(url) ?? convertSinglePhotoFlickrUrl(url)
}

/**
 * Convert:
 * https://www.flickr.com/photos/bucky-badger/albums/72177720321204646 to
 * https://embedr.flickr.com/photosets/72177720321204646
 *
 * @param {string} url The Flickr URL to convert.
 * @returns {string|null} The converted URL, or null if the URL is not an album URL.
 */
function convertFlickrAlbumUrl(url) {
    const regex = /https:\/\/www\.flickr\.com\/photos\/[^\/]+\/albums\/(\d+)/
    const match = url.match(regex)

    if (match && match[1]) {
        return `https://embedr.flickr.com/photosets/${match[1]}`
    }

    return null
}

/**
 * Convert:
 * https://www.flickr.com/photos/bucky-badger/54067512056/ to
 * https://embedr.flickr.com/photosets/72177720321204646
 *
 * @param {string} url The Flickr URL to convert.
 * @returns {string|null} The converted URL, or null if the URL is not an album URL.
 */
function convertSinglePhotoFlickrUrl(url) {
    const regex = /https:\/\/www\.flickr\.com\/photos\/[^\/]+\/(\d+)/
    const match = url.match(regex)

    if (match && match[1]) {
        return `https://embedr.flickr.com/photos/${match[1]}`
    }

    return null
}
